import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { adaptInvestorProfileAnswers } from 'adaptors/adaptAnswers';
import { PRODUCTS } from 'constants/constants';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import { useProductsList } from 'hooks/useProducts';
import { calculateStrategyScore } from 'utils';

// --- Interfaces & Types ---

interface Question {
    Id: number | string;
    Type: 'MultiAnswer' | string;
    MinimumNumberOfAnswers: number;
    MaximumNumberOfAnswers: number;
}

interface Step {
    Questions: Question[];
}

interface Group {
    Id: string | number;
    Steps: Step[];
    Score?: number;
}

interface Questionnaire {
    Groups: Group[];
    ConcurrencyToken?: string;
    IsComplete?: boolean;
    SubmissionId?: string;
    ExpiryDate?: string;
    SubmissionDate?: string;
    RiskCategory?: string;
    Score?: number;
}

interface UseQuestionnaireOptions {
    clientId?: string | number;
    questionnaireTypeId?: number;
}

// --- Query keys (kept in sync with useGetCustomService's `getData_${api}` keying) ---

export const QUESTIONNAIRE_QUERY_KEYS = [
    'getData_searchQuestionnairesByContact',
    'getData_filterQuestionnaires',
    'getData_searchQuestionnairesByContactWithSubmissionId',
];

// --- Helpers ---

const isCompletedAndNotExpired = (data: Partial<Questionnaire> | null): boolean => {
    if (!data) return false;
    const hasStatus = data.IsComplete || data.SubmissionId;
    const notExpired = data.ExpiryDate ? (new Date(data.ExpiryDate).getTime() - new Date().getTime() >= 0) : false;

    return !!(hasStatus && notExpired);
};

// --- Hook ---

export const useQuestionnaire = (options: UseQuestionnaireOptions = {}) => {
    const {
        clientId,
        questionnaireTypeId: questionnaireTypeIdOption = 1,
    } = options;

    const { t, i18n: { language } } = useTranslation();
    const {
        isLoadingProducts,
        products,
        errorProducts,
    } = useProductsList();

    const productIdOption = products?.find((product: any) => product?.settings?.productKey === PRODUCTS.aaa)?.id || null;

    const baseEnabled = !!(productIdOption && questionnaireTypeIdOption && clientId);

    // --- Reads (cached via React Query) ---

    const {
        data: contactResponse,
        isLoading: isLoadingContact,
        isFetching: isFetchingContact,
        error: errorContact,
        refetch: refetchContact,
    } = useGetCustomService({
        service: 'compliance',
        api: 'searchQuestionnairesByContact',
        payload: { contactId: clientId, questionnaireTypeId: questionnaireTypeIdOption, language },
        enabled: baseEnabled,
    });

    const {
        data: filterResponse,
        isLoading: isLoadingFilter,
        isFetching: isFetchingFilter,
        error: errorFilter,
        refetch: refetchFilter,
    } = useGetCustomService({
        service: 'compliance',
        api: 'filterQuestionnaires',
        payload: {
            Language: language,
            ContactTypeId: 1,
            QuestionnaireTypeId: questionnaireTypeIdOption,
            ProductIds: [productIdOption],
        },
        enabled: baseEnabled,
    });

    const questionnaireMeta = contactResponse?.data?.Questionnaires?.[0];
    const submissionId = questionnaireMeta?.SubmissionId;
    // Only read the saved submission when it is complete, not expired AND we actually
    // have a SubmissionId — otherwise the request URL contains `undefined` and 404s.
    const submissionEnabled = baseEnabled && isCompletedAndNotExpired(questionnaireMeta) && !!submissionId;

    // Dependent read: only when a completed, non-expired submission exists.
    const {
        data: submissionResponse,
        isLoading: isLoadingSubmission,
        isFetching: isFetchingSubmission,
        error: errorSubmission,
        refetch: refetchSubmission,
    } = useGetCustomService({
        service: 'compliance',
        api: 'searchQuestionnairesByContactWithSubmissionId',
        payload: { contactId: clientId, submissionId, language },
        enabled: submissionEnabled,
    });

    const notSavedQuestionnaire = filterResponse?.data?.Results?.[0] || null;
    const concurrencyToken = notSavedQuestionnaire?.ConcurrencyToken || null;
    const qualityCheckQuestionnaire: Group | null = notSavedQuestionnaire?.Groups?.[1] || null;

    const finalQuestionnaire: Questionnaire | null = useMemo(
        () => (submissionEnabled ? (submissionResponse?.data || null) : notSavedQuestionnaire),
        [submissionEnabled, submissionResponse, notSavedQuestionnaire],
    );

    const fullData: Questionnaire | null = useMemo(() => {
        if (!finalQuestionnaire) return null;

        return {
            ...finalQuestionnaire,
            Score: finalQuestionnaire?.Groups?.[0]?.Score || calculateStrategyScore(questionnaireMeta?.CalculatedProductRiskCategories?.[0]?.ScoreRangePercentage),
            RiskCategory: questionnaireMeta?.CalculatedProductRiskCategories?.[0]?.RiskCategory,
            SubmissionDate: questionnaireMeta?.SubmissionDate,
            ExpiryDate: questionnaireMeta?.ExpiryDate,
            IsComplete: !!questionnaireMeta?.SubmissionId,
        };
    }, [finalQuestionnaire, questionnaireMeta]);

    const isLoading = isLoadingContact || isLoadingFilter || (submissionEnabled && isLoadingSubmission) || isLoadingProducts;
    const isFetching = isFetchingContact || isFetchingFilter || isFetchingSubmission;
    const error = errorContact || errorFilter || errorSubmission || errorProducts;

    // Read latest questionnaire without adding it to getQuestionnaire's deps —
    // keeps getQuestionnaire identity stable so consumer effects don't refetch-loop.
    const finalQuestionnaireRef = useRef<Questionnaire | null>(finalQuestionnaire);

    finalQuestionnaireRef.current = finalQuestionnaire;

    const getQuestionnaire = useCallback(async () => {
        if (!baseEnabled) return null;

        await Promise.all([
            refetchContact(),
            refetchFilter(),
            ...(submissionEnabled ? [refetchSubmission()] : []),
        ]);

        return finalQuestionnaireRef.current ? { Questionnaire: finalQuestionnaireRef.current } : null;
    }, [baseEnabled, submissionEnabled, refetchContact, refetchFilter, refetchSubmission]);

    // --- Validation ---

    const validateQuestionnaire = useCallback(({
        data = finalQuestionnaire,
        groupId,
        answers = {},
        errorMessage,
    }: { data?: Questionnaire | null; groupId?: string | number; answers?: any; errorMessage?: string } = {}) => {
        const errors: Record<string, string> = {};
        const groupToValidate = groupId
            ? (data?.Groups || []).filter(({ Id }) => Id === groupId)
            : (data?.Groups || []);

        const answersToValidate = groupId
            ? answers[groupId] || {}
            : Object.values(answers || {}).reduce((acc: any, group: any) => ({ ...acc, ...group }), {});

        const questions = groupToValidate.flatMap(group => group.Steps.flatMap(step => step.Questions));

        questions.forEach((question) => {
            const {
                Id, Type, MinimumNumberOfAnswers, MaximumNumberOfAnswers,
            } = question;
            const value = answersToValidate[Id];
            const isRequired = Type === 'MultiAnswer' ? MinimumNumberOfAnswers > 0 : true;

            if (!Object.keys(answersToValidate).includes(Id.toString()) && isRequired) {
                errors[Id] = errorMessage || t('validation.mandatoryField');
            }
            if (value?.length < MinimumNumberOfAnswers) {
                errors[Id] = `${t('validation.questionnaire.minimumNumberOfAnswers')}: ${MinimumNumberOfAnswers}`;
            }
            if (value?.length > MaximumNumberOfAnswers) {
                errors[Id] = `${t('validation.questionnaire.maximumNumberOfAnswers')}: ${MaximumNumberOfAnswers}`;
            }
        });

        return { isValid: Object.keys(errors).length === 0, errors };
    }, [finalQuestionnaire, t]);

    // --- Mutations ---

    const postMutation = useGenericAPIMutation();
    const calculateMutation = useGenericAPIMutation();

    const postQuestionnaire = useCallback(async ({
        answers,
        clientId: mClientId,
    }: { answers: any; clientId?: string | number }) => {
        const resClientId = mClientId || clientId;

        const response = await postMutation.mutateAsync({
            service: 'compliance',
            API: 'postAnswers',
            payload: {
                contactId: resClientId,
                data: adaptInvestorProfileAnswers({ answers, concurrencyToken, qualityCheckQuestionnaire }),
            },
        }) as any;

        return response?.data;
    }, [clientId, concurrencyToken, qualityCheckQuestionnaire, postMutation]);

    const calculateRisk = useCallback(async ({ productId }: { productId?: string | number } = {}) => {
        try {
            const response = await calculateMutation.mutateAsync({
                service: 'compliance',
                API: 'calculateRisk',
                payload: { contactId: clientId, ProductIds: [productId || productIdOption], language },
            }) as any;
            const riskData = response?.data?.CalculatedProductRiskCategories?.[0];

            return { ...riskData, submissionId: response?.data?.SubmissionId };
        } catch (err: any) {
            return err;
        }
    }, [clientId, language, productIdOption, calculateMutation]);

    return {
        isCompletedAndNotExpired,
        data: finalQuestionnaire,
        fullData,
        error,
        isLoading,
        isFetching,
        getQuestionnaire,
        validateQuestionnaire,
        dataPost: postMutation.data ?? null,
        errorPost: postMutation.error ?? null,
        isLoadingPost: postMutation.isPending,
        postQuestionnaire,
        calculateRisk,
    };
};
